import React, { useState, useRef, useCallback } from 'react';
import { Plus, Trash2, Loader2, GripVertical } from 'lucide-react';
import { useI18n } from '../utils/i18n';

const TicketChecklist = ({
  items = [],
  loading = false,
  onAddItem,
  onToggleItem,
  onDeleteItem,
  onReorderItems
}) => {
  const { t } = useI18n();
  const [newItemText, setNewItemText] = useState('');
  const [adding, setAdding] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const inputRef = useRef(null);

  const handleAdd = async (e) => {
    e.preventDefault();
    const trimmed = newItemText.trim();
    if (!trimmed) return;
    setAdding(true);
    try {
      await onAddItem(trimmed);
      setNewItemText('');
      // Fokus zurück auf Eingabefeld
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    } finally {
      setAdding(false);
    }
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    try {
      const rect = e.currentTarget.getBoundingClientRect();
      e.dataTransfer.setDragImage(e.currentTarget, rect.width / 2, rect.height / 2);
    } catch (_) {}
  };

  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    setDragOverIndex(null);
    if (isNaN(dragIndex) || dragIndex === dropIndex) return;
    const newOrder = [...items];
    const [moved] = newOrder.splice(dragIndex, 1);
    newOrder.splice(dropIndex, 0, moved);
    onReorderItems(newOrder.map(item => item.id));
  }, [items, onReorderItems]);

  return (
    <div className="ticket-checklist">
      <h4 className="checklist-heading">Checkliste</h4>

      <form onSubmit={handleAdd} className="checklist-add-form">
        <input
          ref={inputRef}
          type="text"
          value={newItemText}
          onChange={(e) => setNewItemText(e.target.value)}
          placeholder="Neues Element..."
          className="checklist-input"
          disabled={adding}
        />
        <button
          type="submit"
          className="checklist-add-btn"
          disabled={adding || !newItemText.trim()}
          title={t('common.add', 'Hinzufügen')}
        >
          {adding ? <Loader2 size={16} className="spin" /> : <Plus size={16} />}
        </button>
      </form>

      {loading ? (
        <div className="checklist-loading">
          <Loader2 size={18} className="spin" /> Lädt…
        </div>
      ) : (
        <ul className="checklist-list">
          {items.length === 0 && (
            <li className="checklist-empty">{t('common.noEntries', 'Noch keine Einträge')}</li>
          )}
          {items.map((item, index) => (
            <li
              key={item.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              className={`checklist-item${item.isCompleted ? ' completed' : ''}${dragOverIndex === index ? ' drag-over' : ''}`}
            >
              <label className="checklist-item-label">
                <input
                  type="checkbox"
                  checked={!!item.isCompleted}
                  onChange={() => onToggleItem(item.id, !item.isCompleted)}
                  className="checklist-checkbox"
                />
                <span className="checklist-item-text">{item.content}</span>
              </label>
              <div className="checklist-item-actions">
                <button
                  onClick={() => onDeleteItem(item.id)}
                  className="checklist-delete-btn"
                  title={t('common.delete', 'Löschen')} aria-label={t('common.delete', 'Löschen')}
                >
                  <Trash2 size={14} />
                </button>
                <div
                  className="checklist-drag-handle"
                  title="Ziehen zum Umsortieren"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <GripVertical size={14} />
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TicketChecklist;
